import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../State/store";

export type NtfyOptions = {
  /** Base URL of your ntfy server (no trailing slash) */
  baseUrl?: string;                 // default: http://localhost:8075
  /** Where to remember last-seen time across refreshes */
  storage?: Storage;                // default: localStorage
  /** Custom key prefix for last-seen values */
  keyPrefix?: string;               // default: "ntfy_since_"
};

export type UseNtfyReturn = {
  isConnected: boolean;
  error: Event | null;
  /** Manually close the stream (optional) */
  close: () => void;
};

/**
 * React hook to subscribe to an ntfy topic over SSE.
 * - Remembers last seen time per-topic (so reconnects can catch up)
 * - Tries to JSON.parse messages; falls back to raw text payload
 * - Auto-reconnect is handled by EventSource internally
 */
export function useNtfy(
  topic: string,
  onMessage: (payload: any) => void,
  opts?: NtfyOptions
): UseNtfyReturn {
  // ✅ useSelector moved inside the hook (valid usage)
  const ntfyUrl = useSelector((state: RootState) => state.authentication.ntfyUrl);

  const baseUrl = opts?.baseUrl ?? ntfyUrl;
  const storage =
    opts?.storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
  const keyPrefix = (opts?.keyPrefix ?? "ntfy_since_").trim();

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Event | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!topic || !baseUrl) return;

    const url = `${baseUrl}/${encodeURIComponent(topic)}/sse`; // Removed `since` parameter

    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
      console.log(`ntfy: connected to topic "${topic}"`);
      setError(null);
    };

    es.onmessage = (e) => {
      let payload: any = e.data;
      try {
        payload = JSON.parse(e.data);
      } catch {
        payload = { type: "text", message: String(e.data ?? "") };
      }

      try {
        onMessage(payload);
      } finally {
        storage?.setItem(`${keyPrefix}${topic}`, new Date().toISOString());
      }
    };

    es.onerror = (evt) => {
      setIsConnected(false);
      console.log(`ntfy: disconnected from topic "${topic}"`);
      setError(evt || null);
    };

    return () => {
      es.close();
      esRef.current = null;
      setIsConnected(false);
    };
  }, [topic, baseUrl]);

  return {
    isConnected,
    error,
    close: () => {
      esRef.current?.close();
      esRef.current = null;
      setIsConnected(false);
    },
  };
}